import * as THREE from "three";
import { OrbitControls }    from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer }   from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass }       from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass }  from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass }  from "three/examples/jsm/postprocessing/OutputPass.js";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import * as materials from './materials.js';

// import * as dat from 'dat.gui';
import * as dat from 'lil-gui';
import Nacelle from './nacelle.js';
import Engineering from './engineering.js';
import Primary from './primary.js';
import Neck from './neck.js';
import Pylon from './pylon.js';
import Boom from './boom.js';
import Sublight from './sublight.js';
import StarTrails from './star_trails.js';
import Flyby from './flyby.js';
import ships from './ships.js';

const COLOR_SELECTORS_ENABLED = true;

export default class Builder {
  
  constructor(window) {
    this.STORAGE_PREFIX = 'STARSHIPGENERATORV01-'; // for localstoage
    this.predefinedShips = ships;
    for (let ship of this.predefinedShips) {
      this.overlayDefaultShipParams(ship); // assign undefined params to safe defaults
    }
    this.window = window;
    this.SKY_COLOUR = 0x000000;
    this.CLEAR_COLOUR = 0x000000;
    this.dirty = true;
    this.dirtyColor = false;
    this.components = [];
    this.controlParams = {};
    this.currentShip = {}; // storage of currently selected predefined ship params independent of control parmas
    this.targetParams = {}; // if a new predefined ship is selected, the target params are stored here
    this.predefinedShipTransitionFrameCounter = 0; // this is decrement to 0 during the predefined ship transition animation
    this.predefinedShipTransitionFrames = 60*1; // the total number of frames to do the transition animation
    this.transitionRate = 0.1;
    this.maxTransitionTime = 2000; // ms for transition. If it takes longer than this it is forced to finish
    this.scaleIncrement = 0.1;
    this.materialNames = ['Standard', 'Bleachy', 'Grid32', 'Gold Desk Model', 'Chrome Desk Model', "Wireframe", "1963"];
    this.materialIndex = 6;
    this.lights = [];
    this.raycaster = new THREE.Raycaster();
    this.lastModification = "";
    this.bbMesh = null; // bounding box mesh
    this.colors = {};
    this.nacelles = [];
    this.sublights = [];
    this.pylons = [];

    this.views = [
      {
        name: "Orbit",
        primary: true,
        orbit: true,
        perspective: true,
        position: new THREE.Vector3(0, 20, 50),
        target: new THREE.Vector3(0, 10, 0),
        left: 0,
        bottom: 0,
        width: 4/5,
        height: 1.0,
        background: new THREE.Color().setRGB( 0.0, 0.0, 0.0, THREE.SRGBColorSpace ),
        eye: [ 0, 0, 15 ],
        up: [ 0, 1, 0 ],
        fov: 40,
        // this.camera = new THREE.PerspectiveCamera( 40, window.innerWidth / window.innerHeight, 5, 2000 );
        // this.camera.position.z = 50;
        update: function(camera, bbCenter, bbSize) {
          // this.orbitControls.target.set(bbCenter.x, bbCenter.y, bbCenter.z);
          camera.lookAt(this.orbitControls.target);
        },
      },
      {
        name: "Front",
        orbit: false,
        perspective: true,
        position: new THREE.Vector3(0, 10, 50),
        target: new THREE.Vector3(0, 10, 0),
        left: 4/5,
        bottom: 3/4,
        width: 1/5,
        height: 1/4,
        background: new THREE.Color().setRGB( 0.0, 0.0, 0.0, THREE.SRGBColorSpace ),
        eye: [ 0, 0, 100 ],
        up: [ 0, 1, 0 ],
        fov: 15,
        update: function(camera, bbCenter, bbSize) {
          camera.position.set(bbCenter.x, bbCenter.y, bbCenter.z + bbSize.length() * 2.1);
          camera.lookAt(bbCenter.x, bbCenter.y, bbCenter.z);
        },
      },
      {
        name: "Top",
        orbit: false,
        perspective: true,
        position: new THREE.Vector3(0, 10, -50),
        target: new THREE.Vector3(0, 10, 0),
        left: 4/5,
        bottom: 2/4,
        width: 1/5,
        height: 1/4,
        background: new THREE.Color().setRGB( 0.0, 0.0, 0.0, THREE.SRGBColorSpace ),
        eye: [ 0, 100, 0 ],
        up: [ 1, 0, 0 ],
        fov: 20,
        update: function(camera, bbCenter, bbSize) {
          camera.position.set(bbCenter.x, bbCenter.y + bbSize.length() * 2.1, bbCenter.z);
          camera.lookAt(bbCenter.x, bbCenter.y, bbCenter.z);
        },
      },
      {
        name: "Right",
        orbit: false,
        perspective: true,
        position: new THREE.Vector3(-50, 10, 0),
        target: new THREE.Vector3(0, 10, 0),
        left: 4/5,
        bottom: 1/4,
        width: 1/5,
        height: 1/4,
        background: new THREE.Color().setRGB( 0.0, 0.0, 0.0, THREE.SRGBColorSpace ),
        eye: [ -100, 0, 0 ],
        up: [ 0, 1, 0 ],
        fov: 20,
        update: function(camera, bbCenter, bbSize) {
          camera.position.set(bbCenter.x - bbSize.length() * 2.1, bbCenter.y, bbCenter.z);
          camera.lookAt(bbCenter.x, bbCenter.y, bbCenter.z);
        },
      },
      {
        name: "Bottom",
        orbit: false,
        perspective: true,
        position: new THREE.Vector3(0, 10, -50),
        target: new THREE.Vector3(0, 10, 0),
        left: 4/5,
        bottom: 0,
        width: 1/5,
        height: 1/4,
        background: new THREE.Color().setRGB( 0.0, 0.0, 0.0, THREE.SRGBColorSpace ),
        eye: [ 0, -100, 0 ],
        up: [ -1, 0, 0 ],
        fov: 20,
        update: function(camera, bbCenter, bbSize) {
          camera.position.set(bbCenter.x, bbCenter.y - bbSize.length() * 2.1, bbCenter.z);
          camera.lookAt(bbCenter.x, bbCenter.y, bbCenter.z);
        },
      },
      // {
      //   name: "Right",
      //   position: new THREE.Vector3(50, 10, 0),
      //   target: new THREE.Vector3(0, 10, 0),
      // },
      // {
      //   name: "Top",
      //   position: new THREE.Vector3(0, 50, 0),
      //   target: new THREE.Vector3(0, 10, 0),
      // },
      // {
      //   name: "Bottom",
      //   position: new THREE.Vector3(0, -50, 0),
      //   target: new THREE.Vector3(0, -10, 0),
      // }
    ]

    this.nonScalableParamPatterns = [
      "angle",
      "ratio",
      "pointiness",
      "segments",
      "offset",
      "skew",
      "skewVertical",
      "skewHorizontal",
      "dishinset",
      "edgdesharpness",
      "dishradius",
      "undercut",
      "rotation",
      "bridgethickness",
      "bridgeradius",
      "bridgeZ",
      "bridgeY",
    ];
    this.scalableParamPatterns = [
      "engineeringZOffset",
    ]; // these override the above in case they are removed by the above


    //prepend user-saved ships:
    this.predefinedShips = this.getSavedShips().concat(this.predefinedShips);

    // generateMaterials(this);

    this.controlConfiguration = {
      // folderName: {paramName: [default, min, max, step]}
      // refer to variable in code as controlParams.folderName_paramName
      primary: {
        y: [-10, -20, 20, 0.01],
        z: [0.5, -15, 15, 0.01],
        radius: [12, 1, 30, 0.01],
        thickness: [4, 1, 20, 0.01],
        widthRatio: [1, 0.01, 10, 0.01],
        pointiness: [0.0, -1.0, 1.5, 0.01],
        // segments: [64, 3, 64, 1],
        bridgeThickness:  [0.8, 0.5, 9, 0.01],
        bridgeRadius: [0.1, 0.01, 1.2, 0.01],
        bridgeWidthRatio: [1, 0.01, 2, 0.01],
        bridgeZ: [-3.0, -1, 3, 0.01],
        bridgeY: [0, -1, 1, 0.01],
        // bridgeSegments: [64, 3, 64, 1],
        notchAngle: [0, 0, Math.PI, 0.01],
        notchAngleAft: [0, 0, Math.PI, 0.01],
        windowsY: [0, -1, 1.0, 0.01],
        windowsRepeatY: [2, 1, 12.0, 0.01],
        windowsRepeatX: [2, 2, 36, 2],
      },
      sublight: {
        y: [5, -20, 30, 0.01],
        x: [2.5, -15, 15, 0.01],
        z: [-3.5, -15, 15, 0.01],
        length: [12, 1, 50, 0.01],
        radius: [1, 0.05, 2, 0.01],
        widthRatio: [1, 0.1, 10, 0.01],
        rotation: [0, -Math.PI, Math.PI, 0.01],
        segments: [4, 3, 16, 1],
        skewVertical: [0, -3.0, 3.0, 0.01],
        skewHorizontal: [0, -3.0, 3.0, 0.01],
        // undercutStart: [0.9, 0.0, 1.0, 0.01],
        // undercut: [0.75, 0, 1.0, 0.01],
      },
      neck: {
        primaryForeOffset: [0.3, 0, 1, 0.01],
        primaryAftOffset: [0.3, -0.3, 1, 0.01],
        engineeringForeOffset: [0.3, 0, 1, 0.01],
        engineeringAftOffset: [0.3, 0, 1, 0.01],
        primaryThickness: [0.15, 0.01, 5, 0.01],
        thickness: [0.15, 0.01, 5, 0.01],
        foretaper: [1.0, 0.01, 3, 0.01],
        afttaper: [1.0, 0.01, 3, 0.01],
      },
      engineering: {
        y: [-25, -40, 0, 0.01],
        z: [6, -10, 20, 0.01],
        length: [10, 1, 50, 0.01],
        radius: [1, 0, 10, 0.01],
        widthRatio: [1, 0.1, 10, 0.01],
        skew: [0, -5, 5, 0.01],
        // segments: [32, 3, 32, 1],
        undercut: [0.75, 0, 1.0, 0.01],
        undercutStart: [0.7, 0, 1.0, 0.01],
        undercutCurve: [2, 1, 10, 0.01],
        dishRadius: [1, 0.3, 1.1, 0.01],
        dishInset: [0, 0, 2.0, 0.01],
        edgeSharpness: [0.0, 0.0, 4.0, 0.01], // 0.0 = circle, >0 = lens/football (3 & 9 pushed outward)
      },
      nacelle: {
        y: [5, -20, 30, 0.01],
        x: [2.5, -15, 15, 0.01],
        z: [-3.5, -15, 15, 0.01],
        length: [12, 1, 50, 0.01],
        radius: [1, 0.2, 12, 0.01],
        widthRatio: [1, 0.1, 10, 0.01],
        rotation: [0, -Math.PI, Math.PI, 0.01],
        segments: [32, 3, 32, 1],
        skew: [0, -1.5, 1.5, 0.01],
        undercutStart: [0.9, 0.0, 1.0, 0.01],
        undercut: [0.75, 0, 1.0, 0.01],
        bussardWidthRatio: [1.0, 0.1, 5.0, 0.01],
        bussardRadius: [1.0, 0.25, 2.0, 0.01],
        bussardCurvature: [1.0, 0.1, 5.0, 0.01],
        bussardSkewVertical: [0.0, -5.0, 5.0, 0.01],
        bussardYOffset: [0.0, -5.0, 5.0, 0.01],
        bussardZOffset: [0.0, -5.0, 5.0, 0.01],
      },
      pylon: {
        nacelleForeOffset: [0.3, -0.3, 1.5, 0.01],
        nacelleAftOffset: [0.3, -0.3, 1.5, 0.01],
        engineeringForeOffset: [-0.3, -0.7, 1.5, 0.01],
        engineeringAftOffset: [-0.3, -0.7, 1.5, 0.01],
        engineeringZOffset: [0, -6, 6, 0.01],
        midPointOffset: [0.01, 0.01, 2.0, 0.01],
        thickness: [0.15, 0.01, 5, 0.01],
        curvature: [0.01, 0.01, 1.01, 0.1], // 0 = sharp elbow, 1 = fully spread arc
      },
      nacelleLower: {
        y: [5, -20, 30, 0.01],
        x: [2.5, -15, 15, 0.01],
        z: [-3.5, -15, 15, 0.01],
        length: [12, 1, 50, 0.01],
        radius: [1, 0.2, 12, 0.01],
        widthRatio: [1, 0.1, 10, 0.01],
        rotation: [0, -Math.PI, Math.PI, 0.01],
        segments: [32, 3, 32, 1],
        skew: [0, -1.5, 1.5, 0.01],
        undercutStart: [0.9, 0.0, 1.0, 0.01],
        undercut: [0.75, 0, 1.0, 0.01],
        bussardWidthRatio: [1.0, 0.1, 5.0, 0.01],
        bussardRadius: [1.0, 0.25, 1.0, 0.01],
        bussardCurvature: [1.0, 0.1, 5.0, 0.01],
        bussardSkewVertical: [0.0, -5.0, 5.0, 0.01],
        bussardYOffset: [0.0, -5.0, 5.0, 0.01],
        bussardZOffset: [0.0, -5.0, 5.0, 0.01],
      },
      boomLower: {
      },
      pylonLower: {
        nacelleForeOffset: [0.3, -0.3, 1.5, 0.01],
        nacelleAftOffset: [0.3, -0.3, 1.5, 0.01],
        engineeringForeOffset: [0.3, -0.7, 1.5, 0.01],
        engineeringAftOffset: [0.3, -0.7, 1.5, 0.01],
        engineeringZOffset: [0, -6, 6, 0.01],
        midPointOffset: [0.01, 0.01, 2.0, 0.01],
        thickness: [0.15, 0.01, 5, 0.01],
        curvature: [0.01, 0.01, 1.01, 0.1], // 0 = sharp elbow, 1 = fully spread arc
      },
    };

    this.init();
    if (!this.getConsentStatus()) {
      const consentSelection = confirm("This site uses cookies for some features, like saving your ship designs locally. Click [Cancel] if you do not consent and these features will be turned off");
      if (!consentSelection) {
        this.setConsentStatus(false);
        // window.location.href = "/terms.html";
      } else {
        this.setConsentStatus(true);
      }
    }

    this.initControls();
    this.build();
    const startIndex = 0; // Number.parseInt(Math.random() * (this.predefinedShips.length-1));
    this.setPredefinedShip(this.predefinedShips[startIndex].name);
  }

  addLights(configIndex=0) {
    if (this.lights.length > 0) {
      this.lights.forEach(light => {
        this.scene.remove(light);
      });
    }
    this.lights = [];

    const lightConfigs = [
      this.addLightsLow,
      this.addLightsLow,
      this.addLightsLow,
      this.addLightsLow,
      this.addLightsLow,
      this.addLightsLow,
      this.addLightsDefault,
    ];

    lightConfigs[configIndex].call(this);

  }

  // lighting configurations:
  addLightsDefault() {
    const intensity = 400**2;
    const dist = 300;
    this.lights[ 0 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    this.lights[ 1 ] = new THREE.PointLight( 0xffffff, intensity*4.0, 0 ); //bottom
    this.lights[ 2 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    this.lights[ 3 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    // this.lights[ 4 ] = new THREE.PointLight( 0xffffff, intensity/2, 0 );

    this.lights[ 0 ].position.set( dist, dist*0.1, 0 );
    this.lights[ 1 ].position.set( -dist, 0, 0 );
    this.lights[ 2 ].position.set( 0, 0, dist*2 );
    this.lights[ 3 ].position.set( dist, -dist, 0 );
    // this.lights[ 4 ].position.set(0, 20, 0);

    this.scene.add( this.lights[ 0 ] );
    this.scene.add( this.lights[ 1 ] );
    this.scene.add( this.lights[ 2 ] );
    this.scene.add( this.lights[ 3 ] );
    // this.camera.add( this.lights[ 4 ] );
    // this.scene.add( this.camera );
  };

  addLightsDeskModel() {
    const intensity = 60**2;
    const dist = 90;
    this.lights[ 0 ] = new THREE.PointLight( 0xdddddd, intensity*1, 0 );
    this.lights[ 1 ] = new THREE.PointLight( 0x888888, intensity*0, 0 ); //bottom
    this.lights[ 2 ] = new THREE.PointLight( 0xffffff, intensity*0, 0 );
    this.lights[ 3 ] = new THREE.PointLight( 0xffffff, intensity*0, 0 );
    // this.lights[ 4 ] = new THREE.PointLight( 0xffffff, intensity/2, 0 );

    this.lights[ 0 ].position.set( dist, dist, -dist/2 );
    this.lights[ 1 ].position.set( -dist, -dist, 0 );
    this.lights[ 2 ].position.set( 0, 0, dist*2 );
    this.lights[ 3 ].position.set( dist, -dist, 0 );
    // this.lights[ 4 ].position.set(0, 20, 0);

    // this.scene.add( this.lights[ 0 ] );
    // this.scene.add( this.lights[ 1 ] );
    // this.scene.add( this.lights[ 2 ] );
    // this.scene.add( this.lights[ 3 ] );
    // this.scene.fog = new THREE.FogExp2( 0x000000, 0.004 );
    // this.camera.add( this.lights[ 4 ] );
    // this.scene.add( this.camera );
  };

  addLightsLow() {
    const intensity = 400**2;
    const dist = 300;
    this.lights[ 0 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    this.lights[ 1 ] = new THREE.PointLight( 0xffffff, intensity*1.0, 0 ); //bottom
    this.lights[ 2 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    this.lights[ 3 ] = new THREE.PointLight( 0xffffff, intensity*0.03, 0 );
    // this.lights[ 4 ] = new THREE.PointLight( 0xffffff, intensity/2, 0 );

    this.lights[ 0 ].position.set( dist, dist*0.1, 0 );
    this.lights[ 1 ].position.set( -dist, 0, 0 );
    this.lights[ 2 ].position.set( 0, 0, dist*2 );
    this.lights[ 3 ].position.set( dist, -dist, 0 );
    // this.lights[ 4 ].position.set(0, 20, 0);

    this.scene.add( this.lights[ 0 ] );
    this.scene.add( this.lights[ 1 ] );
    this.scene.add( this.lights[ 2 ] );
    this.scene.add( this.lights[ 3 ] );
    // this.camera.add( this.lights[ 4 ] );
    // this.scene.add( this.camera );
  };

  /**
   *
   * buildShip
   *
   * initially create components
   *
   */

  build() {
    this.ship = new THREE.Group();
    this.ship.name = 'ship';
    this.ship.bbCenter = new THREE.Vector3(0, 0, 0);
    this.ship.bbSize = new THREE.Vector3(0, 0, 0);

    this.primary = new Primary();
    this.mount(this.ship, this.primary.group);

    this.sublightPort = new Sublight({name: 'sublightPort'});
    this.mount(this.ship, this.sublightPort.group);

    this.sublightStarboard = new Sublight({name: 'sublightStarboard'});
    this.mount(this.ship, this.sublightStarboard.group);

    this.nacelleUpperPort = new Nacelle({name: 'nacelleUpperPort'});
    this.mount(this.ship, this.nacelleUpperPort.group);

    this.nacelleUpperStarboard = new Nacelle({name: 'nacelleUpperStarboard'});
    this.mount(this.ship, this.nacelleUpperStarboard.group);

    this.nacelleLowerPort = new Nacelle({name: 'nacelleLowerPort'});
    this.mount(this.ship, this.nacelleLowerPort.group);

    this.nacelleLowerStarboard = new Nacelle({name: 'nacelleLowerStarboard'});
    this.mount(this.ship, this.nacelleLowerStarboard.group);

    this.engineering = new Engineering({name: 'engineering'});
    this.mount(this.ship, this.engineering.group);

    this.boomLowerPort = new Boom({name: 'boomLowerPort'});
      this.mount(this.ship, this.boomLowerPort.group);
    
    this.boomLowerStarboard = new Boom({name: 'boomLowerStarboard'});
      this.mount(this.ship, this.boomLowerStarboard.group);

    this.neck = new Neck({
      primary: this.primary,
      engineering: this.engineering,
      name: 'neck',
    });
    this.mount(this.ship, this.neck.group);

    this.portUpperPylon = new Pylon({
      nacelle: this.nacelleUpperPort,
      engineering: this.engineering,
      name: 'portUpperPylon',
    });
    this.mount(this.ship, this.portUpperPylon.group);

    this.starboardUpperPylon = new Pylon({
      nacelle: this.nacelleUpperStarboard,
      engineering: this.engineering,
      name: 'starboardUpperPylon',
    });
    this.mount(this.ship, this.starboardUpperPylon.group);

    this.portLowerPylon = new Pylon({
      nacelle: this.nacelleLowerPort,
      engineering: this.engineering,
      name: 'portLowerPylon',
    });
    this.mount(this.ship, this.portLowerPylon.group);

    this.starboardLowerPylon = new Pylon({
      nacelle: this.nacelleLowerStarboard,
      engineering: this.engineering,
      name: 'starboardLowerPylon',
    });
    this.mount(this.ship, this.starboardLowerPylon.group);

    this.ship.rotateX(Math.PI * 0.5);
    // this.ship.translateY(10.0);

    this.nacelles = [
      this.nacelleLowerPort,
      this.nacelleLowerStarboard,
      this.nacelleUpperPort,
      this.nacelleUpperStarboard,
    ];

    this.sublights = [
      this.sublightPort,
      this.sublightStarboard,
    ];

    this.pylons = [
      this.starboardUpperPylon,
      this.starboardLowerPylon,
      this.portUpperPylon,
      this.portLowerPylon,
    ]

    this.scene.add(this.ship);
  }

  mount(combined, part) {
    combined.add(part);
  }

  update(){
    let controlParams = this.controlParams;

    // nodes

    this.primary.group.visible = this.controlParams.primary_toggle;
    this.primary.update({
      thickness: controlParams.primary_thickness,
      radius: controlParams.primary_radius,
      widthRatio: controlParams.primary_widthRatio,
      pointiness: controlParams.primary_pointiness,
      bridgeThickness: controlParams.primary_bridgeThickness,
      bridgeRadius: controlParams.primary_bridgeRadius,
      bridgeWidthRatio: controlParams.primary_bridgeWidthRatio,
      bridgeZ: controlParams.primary_bridgeZ,
      bridgeY: controlParams.primary_bridgeY,
      notchAngle: controlParams.primary_notchAngle,
      notchAngleAft: controlParams.primary_notchAngleAft,
      segments: 64, // controlParams.primary_segments,
      bridgeSegments: 64, // controlParams.primary_bridgeSegments,
      windowsY: controlParams.primary_windowsY,
      windowsRepeatY: controlParams.primary_windowsRepeatY,
      windowsRepeatX: controlParams.primary_windowsRepeatX,
      materialIndex: this.materialIndex,
    });
    this.primary.group.position.set(0.0, controlParams.primary_y, controlParams.primary_z);


    // sublight
    let separation = controlParams.sublight_x * 2.0;
    let aft = controlParams.sublight_y;
    let height = controlParams.sublight_z;
    let length = controlParams.sublight_length;
    let width = controlParams.sublight_radius;
    let widthRatio = controlParams.sublight_widthRatio;
    let rotation = controlParams.sublight_rotation;
    let segments = controlParams.sublight_segments || 4;
    let skewHorizontal = controlParams.sublight_skewHorizontal || 0;
    let skewVertical = controlParams.sublight_skewVertical || 0;
    let undercutStart = controlParams.sublight_undercutStart || 0;
    let undercut = controlParams.sublight_undercut || 0;

    this.sublightPort.group.visible = this.controlParams.sublight_toggle;
    this.sublightPort.update({length: length, width: width, widthRatio: widthRatio, rotation: rotation, segments: segments, skewVertical: skewVertical, skewHorizontal: skewHorizontal, materialIndex: this.materialIndex, undercutStart: undercutStart, undercut: undercut});
    this.sublightPort.group.position.set(separation, -aft-length, -height);

    this.sublightStarboard.group.visible = this.controlParams.sublight_toggle;
    this.sublightStarboard.update({length: length, width: width, widthRatio: widthRatio, rotation: -rotation, segments: segments, skewVertical: skewVertical, skewHorizontal: -skewHorizontal, materialIndex: this.materialIndex, undercutStart: undercutStart, undercut: undercut});
    this.sublightStarboard.group.position.set(-separation, -aft-length, -height);

    // nacelles upper
    separation = controlParams.nacelle_x * 2.0;
    aft = controlParams.nacelle_y;
    height = controlParams.nacelle_z;
    length = controlParams.nacelle_length;
    width = controlParams.nacelle_radius;
    widthRatio = controlParams.nacelle_widthRatio;
    rotation = controlParams.nacelle_rotation;
    segments = controlParams.nacelle_segments || 32;
    let skew = controlParams.nacelle_skew || 0;
    undercutStart = controlParams.nacelle_undercutStart || 0;
    undercut = controlParams.nacelle_undercut || 0;
    let bussardWidthRatio = controlParams.nacelle_bussardWidthRatio || 1.0;
    let bussardRadius = controlParams.nacelle_bussardRadius || 1.0;
    let bussardCurvature = controlParams.nacelle_bussardCurvature || 1.0;
    let bussardZOffset = controlParams.nacelle_bussardZOffset || 0.0;
    let bussardYOffset = controlParams.nacelle_bussardYOffset || 0.0;
    let bussardSkewVertical = controlParams.nacelle_bussardSkewVertical || 0.0;

    this.nacelleUpperPort.group.visible = this.controlParams.nacelle_toggle;
    this.nacelleUpperPort.update({
      length: length,
      width: width,
      widthRatio: widthRatio,
      rotation: rotation,
      segments: segments,
      skew: skew,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart,
      undercut: undercut,
      bussardWidthRatio: bussardWidthRatio,
      bussardRadius: bussardRadius,
      bussardCurvature: bussardCurvature,
      bussardZOffset: bussardZOffset,
      bussardYOffset: bussardYOffset,
      bussardSkewVertical: bussardSkewVertical,
    });
    this.nacelleUpperPort.group.position.set(separation, -aft-length, -height);

    this.nacelleUpperStarboard.group.visible = this.controlParams.nacelle_toggle;
    this.nacelleUpperStarboard.update({
      length: length,
      width: width,
      widthRatio: widthRatio,
      rotation: -rotation,
      segments: segments,
      skew: skew,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart,
      undercut: undercut,
      bussardWidthRatio: bussardWidthRatio,
      bussardRadius: bussardRadius,
      bussardCurvature: bussardCurvature,
      bussardZOffset: bussardZOffset,
      bussardYOffset: bussardYOffset,
      bussardSkewVertical: bussardSkewVertical,
    });
    
    this.nacelleUpperStarboard.group.position.set(-separation, -aft-length, -height);

    // nacelles lower
    let separation2 = controlParams.nacelleLower_x * 2.0;
    let aft2 = controlParams.nacelleLower_y;
    let height2 = controlParams.nacelleLower_z;
    let length2 = controlParams.nacelleLower_length;
    let width2 = controlParams.nacelleLower_radius;
    let widthRatio2 = controlParams.nacelleLower_widthRatio;
    let rotation2 = controlParams.nacelleLower_rotation;
    let segments2 = controlParams.nacelleLower_segments || 32;
    let skew2 = controlParams.nacelleLower_skew || 0;
    let undercutStart2 = controlParams.nacelleLower_undercutStart || 0;
    let undercut2 = controlParams.nacelleLower_undercut || 0;
    let bussardWidthRatio2 = controlParams.nacelleLower_bussardWidthRatio || 1.0;
    let bussardRadius2 = controlParams.nacelleLower_bussardRadius || 1.0;
    let bussardCurvature2 = controlParams.nacelleLower_bussardCurvature || 1.0;
    let bussardZOffset2 = controlParams.nacelleLower_bussardZOffset || 0.0;
    let bussardYOffset2 = controlParams.nacelleLower_bussardYOffset || 0.0;
    let bussardSkewVertical2 = controlParams.nacelleLower_bussardSkewVertical || 0.0;

    this.nacelleLowerPort.group.visible = this.controlParams.nacelleLower_toggle;

    this.nacelleLowerPort.update({
      length: length2,
      width: width2,
      widthRatio: widthRatio2,
      rotation: rotation2,
      segments: segments2,
      skew: skew2,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart2,
      undercut: undercut2,
      bussardWidthRatio: bussardWidthRatio2,
      bussardRadius: bussardRadius2,
      bussardCurvature: bussardCurvature2,
      bussardZOffset: bussardZOffset2,
      bussardYOffset: bussardYOffset2,
      bussardSkewVertical: bussardSkewVertical2,
    });

    this.nacelleLowerPort.group.position.set(separation2, -aft2-length2, height2);

    this.nacelleLowerStarboard.group.visible = this.controlParams.nacelleLower_toggle;
    this.nacelleLowerStarboard.update({
      length: length2,
      width: width2,
      widthRatio: widthRatio2,
      rotation: -rotation2,
      segments: segments2,
      skew: skew2,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart2,
      undercut: undercut2,
      bussardWidthRatio: bussardWidthRatio2,
      bussardRadius: bussardRadius2,
      bussardCurvature: bussardCurvature2,
      bussardZOffset: bussardZOffset2,
      bussardYOffset: bussardYOffset2,
      bussardSkewVertical: bussardSkewVertical2,
    });

    this.nacelleLowerStarboard.group.position.set(-separation2, -aft2-length2, height2);

    this.boomLowerPort.group.visible = this.controlParams.boomLower_toggle;
    this.boomLowerPort.update({
      length: length2,
      width: width2,
      widthRatio: widthRatio2,
      rotation: rotation2,
      segments: segments2,
      skew: skew2,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart2,
      undercut: undercut2,
    });

    this.boomLowerPort.group.position.set(separation2, -aft2-length2, height2);

    this.boomLowerStarboard.group.visible = this.controlParams.boomLower_toggle;
    this.boomLowerStarboard.update({
      length: length2,
      width: width2,
      widthRatio: widthRatio2,
      rotation: -rotation2,
      segments: segments2,
      skew: skew2,
      materialIndex: this.materialIndex,
      undercutStart: undercutStart2,
      undercut: undercut2,
    });

    this.boomLowerStarboard.group.position.set(-separation2, -aft2-length2, height2);

    this.engineering.group.visible = this.controlParams.engineering_toggle;
    this.engineering.update ({
      length: controlParams.engineering_length,
      width: controlParams.engineering_radius,
      widthRatio: controlParams.engineering_widthRatio,
      skew: controlParams.engineering_skew,
      segments: 64, // controlParams.engineering_segments || 32,
      undercut: controlParams.engineering_undercut,
      undercutStart: controlParams.engineering_undercutStart,
      undercutCurve: controlParams.engineering_undercutCurve,
      dishRadius: controlParams.engineering_dishRadius,
      dishInset: controlParams.engineering_dishInset,
      edgeSharpness: controlParams.engineering_edgeSharpness || 0.0,
      materialIndex: this.materialIndex,
    });
    this.engineering.group.position.set(0.0, controlParams.engineering_y, controlParams.engineering_z);

    // edges
    this.portUpperPylon.group.visible = this.controlParams.pylon_toggle;
    this.portUpperPylon.update({
      nacelleForeOffset: controlParams.pylon_nacelleForeOffset,
      nacelleAftOffset: controlParams.pylon_nacelleAftOffset,
      engineeringForeOffset: controlParams.pylon_engineeringForeOffset,
      engineeringAftOffset: controlParams.pylon_engineeringAftOffset,
      midpointOffset: controlParams.pylon_midPointOffset,
      thickness: controlParams.pylon_thickness,
      materialIndex: this.materialIndex,
      engineeringZOffset: controlParams.pylon_engineeringZOffset,
      curvature: controlParams.pylon_curvature,
    });

    this.starboardUpperPylon.group.visible = this.controlParams.pylon_toggle;
    this.starboardUpperPylon.update({
      nacelleForeOffset: controlParams.pylon_nacelleForeOffset,
      nacelleAftOffset: controlParams.pylon_nacelleAftOffset,
      engineeringForeOffset: controlParams.pylon_engineeringForeOffset,
      engineeringAftOffset: controlParams.pylon_engineeringAftOffset,
      midpointOffset: controlParams.pylon_midPointOffset,
      thickness: controlParams.pylon_thickness,
      materialIndex: this.materialIndex,
      engineeringZOffset: controlParams.pylon_engineeringZOffset,
      curvature: controlParams.pylon_curvature,
    });

    this.portLowerPylon.group.visible = this.controlParams.pylonLower_toggle;
    this.portLowerPylon.update({
      nacelleForeOffset: controlParams.pylonLower_nacelleForeOffset,
      nacelleAftOffset: controlParams.pylonLower_nacelleAftOffset,
      engineeringForeOffset: controlParams.pylonLower_engineeringForeOffset,
      engineeringAftOffset: controlParams.pylonLower_engineeringAftOffset,
      midpointOffset: controlParams.pylonLower_midPointOffset,
      thickness: controlParams.pylonLower_thickness,
      materialIndex: this.materialIndex,
      engineeringZOffset: controlParams.pylonLower_engineeringZOffset,
      curvature: controlParams.pylonLower_curvature,
    });

    this.starboardLowerPylon.group.visible = this.controlParams.pylonLower_toggle;
    this.starboardLowerPylon.update({
      nacelleForeOffset: controlParams.pylonLower_nacelleForeOffset,
      nacelleAftOffset: controlParams.pylonLower_nacelleAftOffset,
      engineeringForeOffset: controlParams.pylonLower_engineeringForeOffset,
      engineeringAftOffset: controlParams.pylonLower_engineeringAftOffset,
      midpointOffset: controlParams.pylonLower_midPointOffset,
      thickness: controlParams.pylonLower_thickness,
      materialIndex: this.materialIndex,
      engineeringZOffset: controlParams.pylonLower_engineeringZOffset,
      curvature: controlParams.pylonLower_curvature,
    });

    this.neck.group.visible = this.controlParams.neck_toggle;
    this.neck.update({
      primaryForeOffset: controlParams.neck_primaryForeOffset,
      primaryAftOffset: controlParams.neck_primaryAftOffset,
      engineeringForeOffset: controlParams.neck_engineeringForeOffset,
      engineeringAftOffset :controlParams.neck_engineeringAftOffset,
      primaryThickness: controlParams.neck_primaryThickness,
      thickness: controlParams.neck_thickness,
      foretaper: controlParams.neck_foretaper,
      afttaper: controlParams.neck_afttaper,
      materialIndex: this.materialIndex,
    });

    // compute bounding box of ship for cameras:
    // this.ship.boundingBox.max|min.x|y|z
    // const shipVisibleBB = this.getVisibleShipComponents();
    // shipVisibleBB.rotateX(Math.PI * 0.5);
    let bb = new THREE.Box3().setFromObject(this.ship);

    // bb.min.sub(this.ship.position);
    // bb.max.sub(this.ship.position);

    let wireframe_material = new THREE.MeshBasicMaterial( { color: 0x00ff00, wireframe: true } );
    let bbGeometry = new THREE.BoxGeometry( bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z );
    bbGeometry.translate(
      (bb.max.x + bb.min.x) / 2,
      (bb.max.y + bb.min.y) / 2,
      (bb.max.z + bb.min.z) / 2
    );
    if (this.bbMesh) {
      this.scene.remove(this.bbMesh);
    }
    this.bbMesh = new THREE.Mesh(bbGeometry, wireframe_material);
    // this.scene.add(this.bbMesh);

    this.ship.bbCenter = new THREE.Vector3(
      (bb.max.x + bb.min.x) / 2,
      (bb.max.y + bb.min.y) / 2,
      (bb.max.z + bb.min.z) / 2
    );

    this.ship.bbSize = bb.getSize(new THREE.Vector3());

    // make sure camera is orbiting the new position of the saucer:
    // const targetPosition = new THREE.Vector3();
    // this.engineering.group.getWorldPosition(targetPosition);
    // this.controls.target.copy(targetPosition);
    // // this.camera.lookAt(this.controls.target);
    // this.controls.update();
  }

  updateColor() {
    if (!COLOR_SELECTORS_ENABLED){
      return;
    }
    // assign color picker values:
    this.primary.setColor(this.colors.hull);
    this.primary.setEmissive(this.colors.window); 

    this.neck.setColor(this.colors.hull);
    this.neck.setEmissive(this.colors.window);

    this.boomLowerPort.setColor(this.colors.hull);
    this.boomLowerPort.setEmissive(this.colors.window);
    this.boomLowerStarboard.setColor(this.colors.hull);
    this.boomLowerPort.setEmissive(this.colors.window);

    this.engineering.setColor(this.colors.hull);
    this.engineering.setEmissive(this.colors.window);
    this.engineering.setDeflectorColor(this.colors.deflector);

    for (let i in this.nacelles) {
      this.nacelles[i].setColor(this.colors.hull);
      this.nacelles[i].setEmissive(this.colors.bussard);
      this.nacelles[i].setWarpField(this.colors.warpfield);
    }
    for (let i in this.sublights) {
      this.sublights[i].setColor(this.colors.hull);
      this.sublights[i].setEmissive(this.colors.sublight);
    }
    for (let i in this.pylons) {
      this.pylons[i].setColor(this.colors.hull);
      this.pylons[i].setEmissive(this.colors.window);
    }
  }

  init() {
    this.window.addEventListener( 'resize', this.handleWindowResize.bind(this), false );
    this.clock = new THREE.Clock();
    this.container = document.getElementById( 'container' );
    this.shipNameLabel = document.getElementById( 'ship-name' );
    this.btnNext = document.getElementById('next');
    this.btnPrev = document.getElementById('prev');
    this.btnScreenshot = document.getElementById('screenshot');
    this.btnTexture = document.getElementById('texture-next');
    this.btnFlyby = document.getElementById('flyby');

    // camera & controls
    // this.camera = new THREE.PerspectiveCamera( 40, window.innerWidth / window.innerHeight, 1, 2000 );
    // this.camera.position.z = 50;
    // this.controls = new OrbitControls( this.camera, this.container );
    // this.controls.enableDamping = true;
    // this.controls.zoomSpeed = 0.5;

    // set up cameras:
    for ( let ii = 0; ii < this.views.length; ++ ii ) {
      const view = this.views[ ii ];
      let camera = {};
      if(view.perspective) {
        camera = new THREE.PerspectiveCamera( view.fov, window.innerWidth / window.innerHeight, 1, 2000 );
      } else {
        camera = new THREE.OrthographicCamera(
          -100*view.width,100*view.width,-100*view.width,100*view.width, // left, right, top, bottom
          1, 10000 // near, far
        );
      }
      
      if (view.orbit) {
        this.controls = new OrbitControls( camera, this.container );
        this.controls.enableDamping = true;
        this.controls.zoomSpeed = 0.5;
        view.orbitControls = this.controls;
      }
      camera.position.fromArray( view.eye );
      camera.up.fromArray( view.up );
      view.camera = camera;
    }

    // cinematic auto-flyby: swoops the primary orbit camera around the ship, then
    // puts it back exactly where the user left it (see flyby.js)
    this.flyby = new Flyby( {
      camera: this.views[ 0 ].camera,
      controls: this.controls,
      onStateChange: () => this.refreshFlybyControls(),
    } );
    this.flybyControllers = []; // lil-gui refs, filled in by initControls

    // this.controls.screenSpacePanning = false;
    // this.controls.lookSpeed = 0.3;

    // scenes
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color( this.SKY_COLOUR );
    // add skybox:
    //https://tools.wwwtyro.net/space-3d/index.html#animationSpeed=1&fov=80&nebulae=true&pointStars=true&resolution=2048&seed=ad3fw753fffegrf48&stars=true&sun=true    
    // star texture: The sun erupted with an X1.7-class solar flare on May 12, 2013. The flare appears as the bright point on the left of the sun in this full disk view NASA's Solar Dynamics Observatory.
    const cubeLoader = new THREE.CubeTextureLoader();
    cubeLoader.setPath("./images/cubemap/map3/");

    const skyboxTexture = cubeLoader.load
    ([
      "right.jpg", // +X (right)
      "left.jpg", // -X (left)
      "top.jpg", // +Y (top)
      "bottom.jpg", // -Y (bottom)
      "front.jpg", // +Z (front)
      "back.jpg",  // -Z (back)
    ]);

    this.scene.background = skyboxTexture;
    // this.scene.environment = skyboxTexture;

    // animated star trails (warp streaks) flying past the ship
    this.starTrails = new StarTrails( { scene: this.scene } );

    // const material = new THREE.MeshBasicMaterial( { color: 0xffffff, envMap: textureCube } );

    // lights
    this.addLights(this.materialIndex);

    // renderer
    this.renderer = new THREE.WebGLRenderer({
      depth: true,
      antialias: true,
      toneMapping: THREE.ACESFilmicToneMapping,
      toneMappingExposure: 2.0,
      preserveDrawingBuffer: true, // for screenshotting
    });
    this.renderer.shadowMap.enabled = false;
    this.renderer.setClearColor( this.CLEAR_COLOUR );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( window.innerWidth, window.innerHeight );

    // Create the EffectComposer
    this.composer = new EffectComposer(this.renderer);

    // Add RenderPass
    const renderPass = new RenderPass(this.scene, this.views[0].camera);
    this.composer.addPass(renderPass);

    // Add UnrealBloomPass
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.5, // Strength
      0.0, // Radius
      0.7 // Threshold
    );
    this.composer.addPass(bloomPass);

    const outputPass = new OutputPass();
    this.composer.addPass( outputPass );
    

    this.container.innerHTML = "";
    this.container.appendChild( this.renderer.domElement );
    
    this.onPointerDown = this.onPointerDown.bind(this);
    window.addEventListener('pointerdown', this.onPointerDown);
  }

  onPointerDown(event) {
    // TODO - the offest due to the subviews causes a misalignment here:
    // Normalize mouse coordinates (-1 to +1)
    const mouse = new THREE.Vector2();
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = - (event.clientY / window.innerHeight) * 2 + 1;
    
    this.raycaster.setFromCamera(mouse, this.views[0].camera);
    
    function isEffectivelyVisible(obj) {
      while (obj) {
        if (!obj.visible) return false;
        obj = obj.parent;
      }
      return true;
    }

    const intersects = this.raycaster.intersectObjects(this.scene.children, true).filter((i) => isEffectivelyVisible(i.object));
    if (intersects.length > 0) {
      for (let i = 0; i < intersects.length; i++) {
        const intersect = intersects[i];
        if (intersect.object.name) {
          // console.log(intersect.object.name);
          return;
        }
      }
    }
  }

  handleWindowResize() {
    // this.camera.aspect = this.window.innerWidth / this.window.innerHeight;
    // this.camera.updateProjectionMatrix();
    for ( let ii = 0; ii < this.views.length; ++ ii ) {
      const view = this.views[ ii ];
      const camera = view.camera;
      camera.aspect = this.window.innerWidth / this.window.innerHeight;
      camera.updateProjectionMatrix();
    }
    this.renderer.setSize( this.window.innerWidth, this.window.innerHeight );
  }

  render() {
    if (this.predefinedShipTransitionFrameCounter >= 0) {
      this.predefinedShipTransitionFrameCounter--;
      this.updatePredefinedShipTransitionAnimation();
    }
    // only update the ship if something has changed in the params
    if (this.dirty) {
      this.update();
      this.updateColor();
      this.dirty = false;
    }

    if (this.dirtyColor) {
      this.updateColor();
      this.dirtyColor = false;
    }

    const elapsed = shipBuilder.clock.getElapsedTime();
    const angle = elapsed * 2.0 % 100.0;

    for (let i in this.nacelles) {
      this.nacelles[i].rotateBussard(angle);
    }

    for (let i in this.sublights) {
      this.sublights[i].rotateGrill(angle);
    }

    // the star trails only need the elapsed time: the GPU animates them
    if (this.starTrails) {
      this.starTrails.update( elapsed );
    }

    if (this.isFlybyActive()) {
      // the flyby drives the primary orbit camera itself this frame; OrbitControls
      // would re-frame the camera at its own target, so it sits out
      this.flyby.update( elapsed );
      if (!this.flyby.isActive()) this.updateFlybyButton();
    } else {
      this.controls.update( shipBuilder.clock.getDelta() );
    }

    const windowWidth = this.window.innerWidth;
    const windowHeight = this.window.innerHeight;

    // a flyby owns the whole screen: no sub-views competing with the shot
    const showThreeView = !this.isFlybyActive() &&
      windowWidth >= 1000 && windowWidth/windowHeight >= 1.0;

    for ( let ii = 0; ii < this.views.length; ++ ii ) {
      const view = this.views[ ii ];
      const camera = view.camera;
      if (!showThreeView && !view.primary) {
        continue; // skip rendering this view if we're in mobile mode
      }

      // the primary view is positioned by the flyby while one is playing
      if (!(this.isFlybyActive() && view.primary)) {
        view.update(camera, this.ship.bbCenter, this.ship.bbSize);
      }

      let left = Math.floor( windowWidth * view.left );
      let bottom = Math.floor( windowHeight * view.bottom );
      let width = Math.floor( windowWidth * view.width );
      let height = Math.floor( windowHeight * view.height );

      // if mobile, we hide threeview but primary has to take up the full space
      if (!showThreeView && view.primary) {
        left = 0;
        bottom = 0;
        width = windowWidth;
        height = windowHeight;
      }

      this.renderer.setViewport( left, bottom, width, height );
      this.renderer.setScissor( left, bottom, width, height );
      this.renderer.setScissorTest( true );
      this.renderer.setClearColor( view.background );

      camera.aspect = width / height;

      // const targetPosition = new THREE.Vector3();
      // this.engineering.group.getWorldPosition(targetPosition);
      // camera.lookAt(this.controls.target);
      camera.updateProjectionMatrix();
      if(view.orbit) {
        this.composer.render();
      } else {
        this.renderer.render( this.scene, camera );
      }
    }
    // this.composer.render();
  }

  getParamsRoundedJSON() {
    var roundedParams = Object.assign({}, this.controlParams);
    for (var param in roundedParams) {
      if ( typeof roundedParams[param] === "number" ) {
        roundedParams[param] = parseFloat( parseFloat(roundedParams[param]).toFixed(2) );
      }
    }
    return roundedParams;
  }

  paramDump() {
    var roundedParams = this.getParamsRoundedJSON();
    let pretty = JSON.stringify(roundedParams, null, 2)
    return pretty;
  }

  getConsentStatus() {
    const consent =  localStorage[`${this.STORAGE_PREFIX}_consent`]
    console.log("builder:getConsentStatus: ", consent);
    return consent ? true : false;
  }

  setConsentStatus(value) {
    if (value) {
      localStorage.setItem(`${this.STORAGE_PREFIX}_consent`, true);
    } else {
      localStorage.removeItem(`${this.STORAGE_PREFIX}_consent`);
    }
  }

  getSavedShipNames() {
    return Object.keys(localStorage)
      .filter(k => k.startsWith(this.STORAGE_PREFIX) && k !== `${this.STORAGE_PREFIX}_consent`)
      .map(k => k.replace(this.STORAGE_PREFIX, ''));
  }

  assignIfUndefined(paramsObject, paramName, defaultValue) {
    if (paramsObject[paramName] == undefined) {
      paramsObject[paramName] = defaultValue;
    }
  }
  
  overlayDefaultShipParams(params) {
    // as new features and params are added, we have to have sane defaults for saved ships that lack these params:

    this.assignIfUndefined(params, "nacelle_skew", 0.0);
    this.assignIfUndefined(params, "nacelle_bussardCurvature", 1.0);
    this.assignIfUndefined(params, "nacelle_bussardRadius", 1.0);
    this.assignIfUndefined(params, "nacelle_bussardWidthRatio", 1.0);
    this.assignIfUndefined(params, "nacelle_bussardYOffset", 0.0);
    this.assignIfUndefined(params, "nacelle_bussardZOffset", 0.0);
    this.assignIfUndefined(params, "nacelle_bussardSkewVertical", 0.0);
    
    this.assignIfUndefined(params, "nacelleLower_skew", 0.0);
    this.assignIfUndefined(params, "nacelleLower_bussardCurvature", 1.0);
    this.assignIfUndefined(params, "nacelleLower_bussardRadius", 1.0);
    this.assignIfUndefined(params, "nacelleLower_bussardWidthRatio", 1.0);
    this.assignIfUndefined(params, "nacelleLower_bussardYOffset", 0.0);
    this.assignIfUndefined(params, "nacelleLower_bussardZOffset", 0.0);
    this.assignIfUndefined(params, "nacelleLower_bussardSkewVertical", 0.0);
    
    this.assignIfUndefined(params, "sublight_toggle", true);
    this.assignIfUndefined(params, "sublight_skewHorizontal", 0.2);
    this.assignIfUndefined(params, "sublight_skewVertical", 0);
    this.assignIfUndefined(params, "sublight_length", params.primary_radius * 0.5);
    this.assignIfUndefined(params, "sublight_widthRatio", 5);
    this.assignIfUndefined(params, "sublight_radius", params.primary_radius * 0.035);
    this.assignIfUndefined(params, "sublight_rotation", 0);
    this.assignIfUndefined(params, "sublight_segments", 4);
    this.assignIfUndefined(params, "sublight_x", params.primary_radius * 0.06);
    this.assignIfUndefined(params, "sublight_z", -params.primary_z - params.primary_thickness * 0.5);
    this.assignIfUndefined(params, "sublight_y", -params.primary_y + params.primary_radius * 0.5);
    
    this.assignIfUndefined(params, "primary_notchAngleAft", 0.0);
    this.assignIfUndefined(params, "primary_windowsY", 0.0);
    this.assignIfUndefined(params, "primary_windowsRepeatY", 2.0);
    this.assignIfUndefined(params, "primary_windowsRepeatX", 4.0);

    this.assignIfUndefined(params, "engineering_edgeSharpness", 0.0);
    this.assignIfUndefined(params, "pylon_curvature", 0.01);
    this.assignIfUndefined(params, "pylonLower_curvature", 0.01);
    

    return params;
  }


  getSavedShips() {
    let userDefinedShips = [];
    const userDefinedShipNames = Object.keys(localStorage)
      .filter(k => k.startsWith(this.STORAGE_PREFIX) && k !== `${this.STORAGE_PREFIX}_consent`);
    
    for (let name of userDefinedShipNames) {
      let data = localStorage.getItem(name);
      const parsed = JSON.parse(data);
      this.overlayDefaultShipParams(parsed); // assign undefined params to safe defaults
      // console.log("parsed saved ship: ", parsed);
      userDefinedShips.unshift(parsed);
    }
    userDefinedShips = userDefinedShips.sort( (a,b) => b.saveDate - a.saveDate );
    return userDefinedShips;
  }

  takeScreenshot() {
    const timeDateStamp = Date.now();
    var a = document.createElement('a');
    a.href = this.renderer.domElement.toDataURL().replace("image/png", "image/octet-stream");
    a.download = `${this.currentShip.name} ${timeDateStamp}.png`
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  setPredefinedShip(shipName) {
    // a flyby is tied to the ship that was on screen when it started
    if (this.flyby) this.flyby.cancel();
    this.updateFlybyButton();

    this.shipNameLabel.innerHTML = shipName.toUpperCase();
    if(this.transitionStartTime) {
      window.clearTimeout(this.transitionStartTime);
    }
    this.transitionStartTime = setTimeout(function(){
      this.predefinedShipTransitionFrameCounter = 0; //outta time, let's finish animation
    }.bind(this), this.maxTransitionTime);
    let newParams = this.shipParams(shipName);
    this.currentShip = newParams;
    this.predefinedShipTransitionFrameCounter = this.predefinedShipTransitionFrames;
    for (var param in newParams) {
      if (typeof newParams[param] === "number") {
        this.targetParams[param] = newParams[param];
      } else {
        this.controlParams[param] = newParams[param];
      }
    }
  }

  ga(label, value="") {
    // console.log(label, value);
    if(!this.getConsentStatus()){
      return;
    }
    window.gtag('event', [label, value].join(","), {
      event_category: 'user_interaction',
      event_label: label,
      value: value
    });
  }

  saveShipConfig() {
    if(!this.getConsentStatus()){
      let consentSelection = confirm("To save your ship in your local browser, you must consent to cookies being used");
      if (!consentSelection) {
        this.setConsentStatus(false);
        return;
      } else {
        this.setConsentStatus(true);
      }
    }
    const newName = prompt("Enter ship name");
    if (newName === null || newName === "" ) {
      alert("Ship name cannot be empty");
      return;
    }
    this.ga("save_ship", "");
    if (this.predefinedShips.map( (ship) => ship.name ).includes(newName) &&
        !this.getSavedShipNames().includes(newName)) {
      alert("Ship name already exists in predefined ships - enter a new name, or an existing saved ship name to overwrite");
      return;
    }
    const config = this.getParamsRoundedJSON();
    config.name = newName;
    config.saveDate = Date.now().toString();
    const key = this.STORAGE_PREFIX + config.name;
    localStorage.setItem(key, JSON.stringify(config));
    const saved = localStorage.getItem(key);
    alert(`Saved "${config.name}" to local browser storage`);
    window.location.reload(); // simple way of reinitializing the UI
  }

  loadSavedShip(name){
    // console.log("loadSavedShip: ", name);
    const key = this.STORAGE_PREFIX + name;
    const config = localStorage.getItem(key);
    return config;
  }

  nextPredefinedShip() {
    this.ga("next_ship");
    let index = this.currentShipIndex();
    index = (index + 1) % this.predefinedShips.length;
    this.shipSelector.setValue(this.predefinedShips[index].name);
  }

  prevPredefinedShip() {
    this.ga("prev_ship");
    let index = this.currentShipIndex();
    index -= 1;
    if (index < 0) {
      index += this.predefinedShips.length;
    }
    this.shipSelector.setValue(this.predefinedShips[index].name);
  }

  nextTexture() {
    this.ga("next_texture");
    let materialIndex = (this.materialIndex + 1) % this.materialNames.length;
    if ([3, 4, 5].includes(materialIndex)) {
      materialIndex = 6;
    }
    this.materialSelectorControl.setValue(this.materialNames[materialIndex]);
    // this.handleMaterialIndexChange(this.materialNames[materialIndex]);
  }

  currentShipIndex() {
    return this.predefinedShips.map( function(ship){ return ship.name; } ).indexOf(this.currentShip.name);
  }

  shipParams(shipName) {
    // console.log(shipName);
    const params = this.predefinedShips.find(function (ship) { return ship.name == shipName; });
    // console.log(params);
    return params;
  }

  updatePredefinedShipTransitionAnimation() {
    for (var param in this.targetParams) {
      let target = this.targetParams[param];
      let current = this.controlParams[param];
      let delta = (target - current)
      if (this.predefinedShipTransitionFrameCounter <= 0) {
        delta = (target - current);
      } else {
        delta = delta * this.transitionRate;
      }
      this.controlParams[param] = current + delta;
    }
    this.dirty = true;
  }

  getVisibleShipComponents() {
    const visibleGroup = new THREE.Group();

    // we only add a mesh if all its parents are visible:
    this.ship.traverse((child) => {
      if (!child.isMesh) return;
    
      let current = child;
      let allVisible = true;
    
      // Walk up to the root (but stop at this.ship or null)
      while (current && current !== this.ship.parent) {
        if (!current.visible) {
          allVisible = false;
          break;
        }
        current = current.parent;
      }
    
      if (allVisible) {
        visibleGroup.add(child.clone());
      }
    })
    return visibleGroup;
  }


  exportSTL() {
    alert("This is experimental feature which exports a binary STL file for local download");
    this.ga("export_stl");
    const exporter = new STLExporter();
    const options = { binary: true } // binary is preferred for 3D printing services

    const stlGroup = this.getVisibleShipComponents();

    // we only add a mesh if all its parents are visible:

    // scaling - these are generally too small for  a 3D printing service that defaults to units of mm for STL
    const scaleFactor = 5;
    stlGroup.scale.set(scaleFactor, scaleFactor, scaleFactor);

    const stlData = exporter.parse(stlGroup, options);
    const blob = new Blob([stlData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.currentShip.name}.stl`;
    a.click();
    URL.revokeObjectURL(url);
  }

  handleMaterialIndexChange(value) {
    this.materialIndex = this.materialNames.indexOf(value);
    console.log("materialIndex: ", this.materialIndex);
    this.addLights(this.materialIndex);
    this.dirty = true;
    this.render();
  }

  rescale(dir) {
    for (var param in this.controlParams) {
      if ( typeof this.controlParams[param] === "number" && 
            this.paramNameScalable(param))
      {
        this.controlParams[param] *= ( ( 1.0 + dir * this.scaleIncrement) );
      }
    }
    this.dirty = true;
  }

  paramNameScalable(param){
    var p = param.toLowerCase();
    const scalable = this.nonScalableParamPatterns.every((str) => p.indexOf(str) < 0);
    const overrideScalable = this.scalableParamPatterns.some((str) => p.indexOf(str) >= 0);
    return scalable || overrideScalable;
  }

  handleOpenCloseFolder(changedGUI) {
    const clickedFolder = changedGUI._title
    for (var folder in this.gui.folders) {
      if (this.gui.folders[folder]._title !== clickedFolder) {
       this.gui.folders[folder].close();
      }
    }
  }

  setGuiVisibility(visible) {
    const guiNode = document.getElementById('gui-container');
    guiNode.style.opacity = visible ? 0.9 : 0.1;
  }

  initControls(){
    // non datgui controls:
    this.btnNext.addEventListener('click', function(){ this.nextPredefinedShip() }.bind(this));
    this.btnPrev.addEventListener('click', function(){ this.prevPredefinedShip() }.bind(this));
    this.btnScreenshot.addEventListener('click', function(){ this.takeScreenshot() }.bind(this));
    this.btnTexture.addEventListener('click', function(){ this.nextTexture() }.bind(this));
    this.btnFlyby.addEventListener('click', function(event){
      event.preventDefault();
      this.toggleFlyby();
    }.bind(this));

    this.gui = new dat.GUI( { autoPlace: false, touchStyles: false } );
    const gui = this.gui;
    this.gui.$title.innerHTML = "🔧 Customize, Load, Save Ships";
    gui.close();
    gui.onOpenClose( changedGUI => {
      this.handleOpenCloseFolder( changedGUI );
    } );
    document.getElementById('gui-container').appendChild(gui.domElement);
    const guiNode = document.getElementById('gui-container');

    // Find all sliders inside lil-gui when added
    guiNode.addEventListener('touchstart', (e) => {
      if (e.target.closest('.controller.number')) {
        this.setGuiVisibility(false);
      }
    });
    guiNode.addEventListener('mousedown', (e) => {
      if (e.target.closest('.controller.number')) {
        this.setGuiVisibility(false);
      }
    });
    // Show GUI again when touch/mouse ends
    const showOnEnd = (e) => {
      if (e.target.closest('.controller.number')) {
        this.ga(this.lastModification);
      }
      this.setGuiVisibility(true);
    };
    window.addEventListener('touchend', showOnEnd);
    window.addEventListener('mouseup', showOnEnd);
    
    // utils folder
    var utilsFolder = gui.addFolder('save, export, import');

    // screenshot button:
    utilsFolder.add({ save_screen_shot: this.takeScreenshot.bind(this) }, 'save_screen_shot').name('SAVE SCREEN SHOT');
    
    // STL Export:
    utilsFolder.add({ export_stl: this.exportSTL.bind(this) }, 'export_stl').name('EXPORT STL FILE');

    // export button:
    // utilsFolder.add({ copy_ship_params: this.paramDump.bind(this) }, 'copy_ship_params');

    utilsFolder.add({ 
      copy_ship_params: () => {
        const text = this.paramDump();
        // console.log("Ship Parameters:\n\n");
        // console.log(text);
        navigator.clipboard.writeText(text)
          .then(() => alert('Ship params copied to clipboard (for supported browsers)'))
          .catch(err => console.error('Copy to clipboard failed', err));
      }
    }, 'copy_ship_params').name('COPY SHIP PARAMETERS');

    utilsFolder.add({ paste_ship: () => {
      const text = prompt("Paste ship parameters here:");
      try {
        const data = JSON.parse(text);
        // console.log("Parsed object:", data);
        this.predefinedShips.unshift(data);
        this.shipSelector.setValue(this.predefinedShips[0].name);
      } catch (error) {
        // console.error("Invalid JSON:", error.message);
        alert("The input is not valid JSON.");
        return null;
      }
    } }, 'paste_ship').name('PASTE SHIP PARAMETERS');


    utilsFolder.add({ save_ship: this.saveShipConfig.bind(this) }, 'save_ship').name('SAVE SHIP');

    // rescale button:
    utilsFolder.add({ scale_up: this.rescale.bind(this, 1) }, 'scale_up').name('SCALE UP');
    utilsFolder.add({ scale_down: this.rescale.bind(this, -1) }, 'scale_down').name('SCALE DOWN');


    // predefined ships:
    this.currentShip.name = this.predefinedShips[0].name;
    this.shipSelector = gui.add( this.currentShip, 'name', this.predefinedShips.map( (ship) => ship.name ) );

    this.shipSelector.onChange(
      function(newShipName) {
        this.setPredefinedShip(newShipName);
      }.bind(this)
    );

    // Material selector:
    const dummy = {skin: this.materialNames[this.materialIndex]};
    this.materialSelectorControl = gui.add(dummy, 'skin', this.materialNames).name("SKIN").onChange( (value) => { this.handleMaterialIndexChange(value) } );
    // console.log("materialSelectorControl: ", this.materialSelectorControl);

    // params:
    const that = this;
    for (var folder in this.controlConfiguration) {
      var controls = gui.addFolder(folder);
      let paramsInFolder = this.controlConfiguration[folder];
      // add a checkbox per folder
      const toggle_name = folder + '_toggle';
      this.controlParams[toggle_name] = true;

      let toggle = controls.add( this.controlParams, toggle_name, true ).name("VISIBLE");
      toggle.onChange( function(value) {
        toggle.object[toggle.property] = value;
        this.lastModification = toggle.property;
        this.dirty = true;
      }.bind(this));
      toggle.listen();

      for (var key in paramsInFolder) {
        this.controlParams[folder + '_' + key] = paramsInFolder[key][0];
        let ref = controls.add(
          this.controlParams,
          folder + '_' + key,
          paramsInFolder[key][1],
          paramsInFolder[key][2],
          paramsInFolder[key][3]
        ).name(this.splitAtCaps(key));
        ref.onChange(function(){ this.dirty = true; this.lastModification = ref.property; }.bind(this));
        ref.listen();
      }
    }
    
    // Color selectors
    if (COLOR_SELECTORS_ENABLED){
      const colors = {
        hull: 0xffffff,
        window: 0xAA00FF,
        sublight: 0xaa00ff,
        bussard: 0xff9933,
        warpfield: 0x1199ff,
        deflector: 0xaa00ff,
      }
      var colorFolder = gui.addFolder("color");
      for (let key of Object.keys(colors)) {
        let ref = colorFolder.addColor( colors, key );
        ref.onChange( value => {
          this.dirtyColor = true;
          this.colors[key] = value; // hex 0x000000
        } );
      }
    }

    // star trails (warp streaks) - they live outside controlParams, so tweaking
    // them never marks the ship dirty / never triggers a rebuild
    if (this.starTrails) {
      const trails = this.starTrails;
      const trailsFolder = gui.addFolder('star trails');
      trailsFolder.add( trails.params, 'enabled' ).name('VISIBLE')
        .onChange( value => trails.setEnabled( value ) );
      trailsFolder.addColor( trails.params, 'color' ).name('COLOR')
        .onChange( value => trails.setColor( value ) );
      trailsFolder.add( trails.params, 'length', 1, 200, 1 ).name('LENGTH')
        .onChange( value => trails.setLength( value ) );
      trailsFolder.add( trails.params, 'density', 0, 800, 1 ).name('DENSITY / SEC')
        .onChange( value => trails.setDensity( value ) );
      trailsFolder.add( trails.params, 'speed', 50, 1200, 10 ).name('SPEED')
        .onChange( value => trails.setSpeed( value ) );
      trailsFolder.add( trails.params, 'spread', 20, 600, 5 ).name('SPREAD')
        .onChange( value => trails.setSpread( value ) );
      trailsFolder.add( trails.params, 'depth', 200, 4000, 50 ).name('DEPTH')
        .onChange( value => trails.setDepth( value ) );
      trailsFolder.add( trails.params, 'brightness', 0.1, 3, 0.05 ).name('BRIGHTNESS')
        .onChange( value => trails.setBrightness( value ) );
      trailsFolder.add( trails.params, 'reverse' ).name('REVERSE')
        .onChange( value => trails.setReverse( value ) );
    }

    // Camera flyby - a quick pass along the ship: past the bow, along one flank, past
    // the stern, then snap back to the view the user already had (see flyby.js).
    // DURATION is felt immediately; the rest shape the route, which is planned when a
    // flyby starts, so they apply to the next press. CLEARANCE is in ship diagonals -
    // below 0.5 (the ship's bounding sphere) the route will pass through the hull.
    if (this.flyby) {
      const flyby = this.flyby;
      const flybyFolder = gui.addFolder('camera flyby');

      this.flybyControl = flybyFolder
        .add({ flyby: this.toggleFlyby.bind(this) }, 'flyby')
        .name('START FLYBY');

      const addFlybyControl = (key, min, max, step, label, setter) => {
        const ref = flybyFolder.add(flyby.params, key, min, max, step).name(label);
        ref.onChange(value => {
          setter(value);
          this.lastModification = 'flyby_' + key;
        });
        this.flybyControllers.push(ref);
        return ref;
      };

      addFlybyControl('duration', 2, 20, 0.5, 'DURATION (SEC)', value => flyby.setDuration(value));
      addFlybyControl('pause', 0, 5, 0.1, 'PAUSE (SEC)', value => flyby.setPause(value));
      addFlybyControl('clearance', 0, 3, 0.01, 'CLEARANCE (DIAG)', value => flyby.setClearance(value));
    }
  }

  // --- camera flyby ---------------------------------------------------------

  isFlybyActive() {
    return !!( this.flyby && this.flyby.isActive() );
  }

  startFlyby() {
    if (!this.flyby || !this.ship) return;
    this.ga('flyby');
    this.flyby.start({
      center: this.ship.bbCenter,
      size: this.ship.bbSize,
    });
  }

  stopFlyby() {
    if (!this.flyby) return;
    this.flyby.cancel();
    this.ga('flyby_stop');
    this.updateFlybyButton();
  }

  toggleFlyby() {
    if ( this.isFlybyActive() ) {
      this.stopFlyby();
    } else {
      this.startFlyby();
    }
  }

  // called by flyby.js whenever it starts or stops, so the sliders stay truthful
  refreshFlybyControls() {
    for (const ref of this.flybyControllers) {
      ref.updateDisplay();
    }
    this.updateFlybyButton();
  }

  updateFlybyButton() {
    const active = this.isFlybyActive();
    if (this.btnFlyby) {
      this.btnFlyby.classList.toggle('active', active);
    }
    if (!this.flybyControl) return;
    this.flybyControl.name( active ? 'STOP FLYBY' : 'START FLYBY' );
  }

  splitAtCaps(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1 $2');
  }
} // class


var shipBuilder = new Builder(window);

function animate() {
  requestAnimationFrame( animate );
  shipBuilder.render();
}
animate();
